import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";

// Define a type for the slice state
export interface LogState {
  recently_viewed: {
    data: any[];
    waiting: boolean;
    loading: boolean;
    errors: any[];
    pagination: {
      has_previous: boolean;
      has_next: boolean;
      current_page: number;
      total_pages: number;
      total_objects: number;
    };
  },
}

// Define the initial state using that type
const initialState: LogState = {
  recently_viewed: {
    data: [],
    waiting: false,
    loading: false,
    errors: [],
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0
    },
  },
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get recently viewed
export const getRecentlyViewed = createAsyncThunk(
  "log/recently-viewed/get",
  async (
    args,
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      const url = `/api/log/recently-viewed`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Mark content as viewed
export const markAsViewed = createAsyncThunk(
  "log/recently-viewed/mark-as-viewed",
  async (
    obj: {
      objectId: string;
      objectType: string;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = {
        headers: {
          "Content-Type": "application/json",
        }
      };

      // Build endpoint URL
      const url = `/api/log/view`;

      const body = JSON.stringify({
        id: obj.objectId,
        profile_id: auth.user === null ? null : auth.user.profile.id,
        object_type: obj.objectType,
      });

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const logSlice = createSlice({
  name: "log",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Get recently viewed
      .addCase(getRecentlyViewed.pending, (state) => {
        state.recently_viewed.waiting = true;
        state.recently_viewed.loading = true;
        state.recently_viewed.errors = [];
      })
      .addCase(getRecentlyViewed.fulfilled, (state, action) => {
        state.recently_viewed.waiting = false;
        state.recently_viewed.loading = false;
        state.recently_viewed.errors = [];
        state.recently_viewed.data = action.payload.data;
      })
      .addCase(getRecentlyViewed.rejected, (state, action) => {
        state.recently_viewed.waiting = false;
        state.recently_viewed.loading = false;
        state.recently_viewed.errors = action.payload ? [action.payload] : ["An error occurred"];
      })

      // Mark as viewed
      .addCase(markAsViewed.pending, (state) => {
      })
      .addCase(markAsViewed.fulfilled, (state, action) => {
      })
      .addCase(markAsViewed.rejected, (state, action) => {
      });

  },
});

export default logSlice.reducer;
